/**
 * What a changed figure of a table says when it is looked at, and where it leads.
 *
 * Shared by the table on the page and by the table in the view of a block's
 * change, so a figure reads the same in both: the tip with what it said and
 * says now, who last changed it where the journal can tell, and a way to its
 * history (CSRD's screen for a table read from there; the cell's own panel for
 * one kept here).
 *
 * The tip is held open for a moment after the pointer leaves, so it can travel
 * onto it and press its link.
 */

import { computed, onBeforeUnmount, ref, type Ref } from 'vue'

import type { TableSection } from '@/domain/section'
import { collectionUrl, csrdWebUrl } from '@/features/csrdLink/screen'
import { targetOf } from '@/features/versions/cellTrail'
import type { CellDiff } from '@/features/versions/table'
import { useCellHistoryStore } from '@/stores/cellHistory'
import { useSnapshotsStore } from '@/stores/snapshots'

/** What is being read: the cell, where it is on the screen, and who last changed it. */
export interface ReadingCell {
  readonly cell: CellDiff
  readonly anchor: DOMRect
  readonly last: { author: string; at: string } | null
}

export function useCellTip(table: () => TableSection): {
  reading: Ref<ReadingCell | null>
  baseNumber: Ref<number>
  screenUrl: Ref<string | null>
  show: (event: MouseEvent, cell: CellDiff | undefined, line: number, column: number) => void
  hold: () => void
  drop: () => void
  open: (cell: CellDiff | undefined, line: number, column: number) => void
} {
  const snapshots = useSnapshotsStore()
  const cellHistory = useCellHistoryStore()

  const reading = ref<ReadingCell | null>(null)
  const webUrl = ref('')
  let closing: ReturnType<typeof setTimeout> | undefined

  const baseNumber = computed(() => snapshots.baseline?.summary.number ?? 0)
  const screenUrl = computed(() => {
    const link = table().csrd
    return link ? collectionUrl(link, webUrl.value) : null
  })

  function hold(): void {
    clearTimeout(closing)
  }

  function drop(): void {
    clearTimeout(closing)
    closing = setTimeout(() => {
      reading.value = null
    }, 160)
  }

  onBeforeUnmount(() => clearTimeout(closing))

  async function ask(cell: CellDiff, anchor: DOMRect, line: number, column: number): Promise<void> {
    const section = table()
    if (section.csrd) {
      if (!webUrl.value) webUrl.value = await csrdWebUrl()
      return
    }

    // Kept here: who last changed it comes from the journal, which is read on demand.
    await cellHistory.entriesOf(section.id)
    const last = cellHistory.trailOf(section.id, targetOf(section, line, column))[0]
    if (reading.value?.anchor === anchor) {
      reading.value = { cell, anchor, last: last ? { author: last.author, at: last.at } : null }
    }
  }

  function show(event: MouseEvent, cell: CellDiff | undefined, line: number, column: number): void {
    if (!cell) return
    hold()
    const anchor = (event.currentTarget as HTMLElement).getBoundingClientRect()
    reading.value = { cell, anchor, last: null }
    void ask(cell, anchor, line, column)
  }

  /**
   * A press on a changed cell of a table kept here: its history, beside the page.
   *
   * A view of the change that is open gives way to it, one dialog at a time.
   */
  function open(cell: CellDiff | undefined, line: number, column: number): void {
    const section = table()
    if (section.csrd || !cell) return
    reading.value = null
    snapshots.inspect(null)
    void cellHistory.show({ sectionId: section.id, target: targetOf(section, line, column) })
  }

  return { reading, baseNumber, screenUrl, show, hold, drop, open }
}
